import React, { useState, useMemo } from 'react';
import MailSidebar from './MailSidebar';
import MailList from './MailList';
import MailView from './MailView';
import MailCompose from './MailCompose';
import { folders, mails as initialMails } from './mailData';
import './Mail.css';

function Mail() {
    const [activeFolder, setActiveFolder] = useState('inbox');
    const [selectedMailId, setSelectedMailId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [mails, setMails] = useState(initialMails);
    const [composeOpen, setComposeOpen] = useState(false);
    const [composeData, setComposeData] = useState(null);
    const [mobileView, setMobileView] = useState('list'); // 'list' | 'view'

    // Фильтрация писем по папке + поиск
    const filteredMails = useMemo(() => {
        let result = mails.filter(m => m.folder === activeFolder);

        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(m =>
                m.subject.toLowerCase().includes(q) ||
                m.from.name.toLowerCase().includes(q) ||
                m.from.email.toLowerCase().includes(q) ||
                m.preview.toLowerCase().includes(q)
            );
        }

        // Сортировка: сначала новые
        return result.sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [mails, activeFolder, searchQuery]);

    const selectedMail = mails.find(m => m.id === selectedMailId) || null;

    // При выборе письма помечаем как прочитанное
    const handleSelectMail = (id) => {
        setSelectedMailId(id);
        setMails(prev => prev.map(m => m.id === id ? { ...m, read: true } : m));
        setMobileView('view');
    };

    // Переключение «звёздочки»
    const handleToggleStar = (id, e) => {
        e.stopPropagation();
        setMails(prev => prev.map(m => m.id === id ? { ...m, starred: !m.starred } : m));
    };

    // Удаление письма
    const handleDelete = (id) => {
        setMails(prev => prev.map(m => m.id === id ? { ...m, folder: 'trash' } : m));
        setSelectedMailId(null);
        setMobileView('list');
    };

    // Открыть «Написать»
    const handleCompose = (data = null) => {
        setComposeData(data);
        setComposeOpen(true);
    };

    // Отправка письма из модалки
    const handleSend = (newMail) => {
        setMails(prev => [
            ...prev,
            {
                ...newMail,
                id: Date.now(),
                folder: 'sent',
                date: new Date().toISOString(),
                read: true,
                starred: false,
                labels: [],
                attachments: [],
            },
        ]);
        setComposeOpen(false);
        setComposeData(null);
    };

    const unreadCount = mails.filter(m => m.folder === 'inbox' && !m.read).length;

    return (
        <div className="mail-page">
            <div className={`mail-layout mobile-${mobileView}`}>
                {/* Левая колонка — папки */}
                <MailSidebar
                    folders={folders}
                    activeFolder={activeFolder}
                    onSelectFolder={(id) => {
                        setActiveFolder(id);
                        setSelectedMailId(null);
                        setMobileView('list');
                    }}
                    unreadCount={unreadCount}
                    onCompose={() => handleCompose()}
                />

                {/* Средняя колонка — список писем */}
                <MailList
                    mails={filteredMails}
                    selectedMailId={selectedMailId}
                    onSelectMail={handleSelectMail}
                    onToggleStar={handleToggleStar}
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    folderName={folders.find(f => f.id === activeFolder)?.name || ''}
                    onCompose={() => handleCompose()}
                />

                {/* Правая колонка — просмотр письма */}
                <MailView
                    mail={selectedMail}
                    onBack={() => {
                        setSelectedMailId(null);
                        setMobileView('list');
                    }}
                    onDelete={handleDelete}
                    onReply={() => {
                        if (selectedMail) {
                            handleCompose({
                                to: selectedMail.from.email,
                                subject: `Re: ${selectedMail.subject}`,
                                body: `\n\n---\n${selectedMail.from.name} писал(а):\n> ${selectedMail.body.split('\n').join('\n> ')}`,
                            });
                        }
                    }}
                    onForward={() => {
                        if (selectedMail) {
                            handleCompose({
                                to: '',
                                subject: `Fwd: ${selectedMail.subject}`,
                                body: `\n\n--- Пересланное сообщение ---\nОт: ${selectedMail.from.name} <${selectedMail.from.email}>\nТема: ${selectedMail.subject}\n\n${selectedMail.body}`,
                            });
                        }
                    }}
                />
            </div>

            {/* Модалка «Написать» */}
            {composeOpen && (
                <MailCompose
                    initialData={composeData}
                    onClose={() => {
                        setComposeOpen(false);
                        setComposeData(null);
                    }}
                    onSend={handleSend}
                />
            )}
        </div>
    );
}

export default Mail;